"use client";

import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OfferLanguage, PricingGrid as PricingGridData } from "@/db/schema/content-items";
import { formatEur, formatDays, formatEurRange, formatDaysRange } from "@/lib/content/pricing-format";

const COPY = {
  fr: {
    title: "Tarifs",
    fixedIntro:
      "Un prix fixe pour tout le projet, réglé jalon par jalon : vous validez chacun avant de passer au suivant.",
    fixedPrice: "Prix fixe",
    ofDev: "de développement",
    optionsIncluded: ", options comprises",
    jalon: "Jalon",
    included: "Ce qui est inclus",
    options: "Options",
    optionsIntro:
      "Seulement si vous souhaitez aller plus loin, chacune à prix fixe. Cochez pour l'ajouter au total.",
    groupsIntro:
      "Le projet avance milestone par milestone : vous validez et réglez chacun avant de passer au suivant.",
    rangesIntro: "Chaque fonctionnalité a sa fourchette. Pas de total générique à prendre ou à laisser.",
    pricesIntro: "Chaque fonctionnalité a son prix. Pas de total générique à prendre ou à laisser.",
    milestone: "Milestone",
    others: "Autres éléments",
    addons: "Compléments activables",
    addonsIntro:
      "Optionnels, seulement si vous souhaitez aller plus loin. Cochez pour les ajouter au total.",
    total: "Total estimé",
    bySelection: "selon vos options sélectionnées",
  },
  en: {
    title: "Pricing",
    fixedIntro:
      "One fixed price for the whole project, paid milestone by milestone: you approve each one before moving to the next.",
    fixedPrice: "Fixed price",
    ofDev: "of development",
    optionsIncluded: ", options included",
    jalon: "Milestone",
    included: "What is included",
    options: "Options",
    optionsIntro:
      "Only if you want to go further, each at a fixed price. Tick to add it to the total.",
    groupsIntro:
      "The project moves forward milestone by milestone: you approve and pay for each one before moving to the next.",
    rangesIntro: "Each feature has its own range. No generic total to take or leave.",
    pricesIntro: "Each feature has its own price. No generic total to take or leave.",
    milestone: "Milestone",
    others: "Other items",
    addons: "Optional add-ons",
    addonsIntro: "Optional, only if you want to go further. Tick to add them to the total.",
    total: "Estimated total",
    bySelection: "based on your selected options",
  },
} satisfies Record<OfferLanguage, Record<string, string>>;

export function PricingGrid({
  grid,
  dark = false,
  lang = "fr",
}: {
  grid: PricingGridData;
  dark?: boolean;
  lang?: OfferLanguage;
}) {
  const t = COPY[lang];
  const base = useMemo(() => grid.lines.filter((l) => l.kind === "base"), [grid.lines]);
  const addons = useMemo(() => grid.lines.filter((l) => l.kind === "addon"), [grid.lines]);

  const [selected, setSelected] = useState<Record<string, boolean>>({});

  // Regroupement par milestone (titre libre saisi en admin), dans l'ordre
  // d'apparition ; les lignes sans milestone restent en fin de liste.
  const groups = useMemo(() => {
    if (!base.some((l) => l.milestone?.trim())) return null;
    const byTitle = new Map<string, PricingGridData["lines"]>();
    for (const l of base) {
      const key = l.milestone?.trim() ?? "";
      byTitle.set(key, [...(byTitle.get(key) ?? []), l]);
    }
    const titled = [...byTitle.entries()].filter(([t]) => t);
    const untitled = byTitle.get("") ?? [];
    return [
      ...titled.map(([title, lines]) => ({ title, lines })),
      ...(untitled.length ? [{ title: "", lines: untitled }] : []),
    ];
  }, [base]);
  const hasRanges = grid.lines.some((l) => l.amountHigh > l.amountLow);

  if (grid.lines.length === 0) return null;

  const baseTotalLow = base.reduce((sum, l) => sum + l.amountLow, 0);
  const baseTotalHigh = base.reduce((sum, l) => sum + l.amountHigh, 0);
  const addonsTotalLow = addons.reduce(
    (sum, l) => (selected[l.id] ? sum + l.amountLow : sum),
    0
  );
  const addonsTotalHigh = addons.reduce(
    (sum, l) => (selected[l.id] ? sum + l.amountHigh : sum),
    0
  );
  const totalLow = baseTotalLow + addonsTotalLow;
  const totalHigh = baseTotalHigh + addonsTotalHigh;

  const baseDaysLow = base.reduce((sum, l) => sum + (l.daysLow || 0), 0);
  const baseDaysHigh = base.reduce((sum, l) => sum + (l.daysHigh || 0), 0);
  const addonsDaysLow = addons.reduce(
    (sum, l) => (selected[l.id] ? sum + (l.daysLow || 0) : sum),
    0
  );
  const addonsDaysHigh = addons.reduce(
    (sum, l) => (selected[l.id] ? sum + (l.daysHigh || 0) : sum),
    0
  );
  const totalDaysLow = baseDaysLow + addonsDaysLow;
  const totalDaysHigh = baseDaysHigh + addonsDaysHigh;

  function renderBaseLine(line: PricingGridData["lines"][number], i: number) {
    return (
      <li
        key={line.id}
        className={cn(
          "flex gap-4 px-5 py-4 sm:px-7 sm:py-5",
          i > 0 && "border-t border-line"
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink">{line.label}</div>
          {line.description && (
            <p className="mt-1 text-base leading-relaxed text-ink/65">
              {line.description}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="font-display text-lg sm:text-xl text-navy tabular-nums">
            {formatEurRange(line.amountLow, line.amountHigh, lang)}
          </div>
          {(line.daysLow > 0 || line.daysHigh > 0) && (
            <div className="mt-0.5 text-sm text-ink/50">
              {formatDaysRange(line.daysLow, line.daysHigh, lang)}
            </div>
          )}
        </div>
      </li>
    );
  }

  function toggle(id: string) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // Prix fixe (grilles générées depuis le 2026-10-12, sans fourchette) : un
  // seul prix en haut, puis le découpage par jalon sans prix par ligne, puis
  // les compléments à prix fixe. Les offres plus anciennes, à fourchettes,
  // gardent l'affichage ligne par ligne plus bas.
  if (!hasRanges) {
    const jalons = groups ?? [{ title: "", lines: base }];
    return (
      <div>
        <h2
          className={cn(
            "font-display font-normal text-4xl sm:text-5xl text-center",
            dark ? "text-paper" : "text-ink"
          )}
        >
          {t.title}
        </h2>
        <p className={cn("mt-3 text-center", dark ? "text-paper/70" : "text-ink/70")}>
          {t.fixedIntro}
        </p>
        <div className={cn("editorial-rule mt-6 mx-auto", dark && "editorial-rule--light")} />

        <div className="mt-10 rounded-xl border border-gold/60 bg-paper-pure p-5 text-center shadow-sm sm:p-7">
          <div className="font-mono text-[0.75rem] uppercase tracking-widest text-gold">
            {t.fixedPrice}
          </div>
          <div className="mt-2 font-display text-5xl text-navy tabular-nums sm:text-6xl">
            {formatEur(totalLow, lang)}
          </div>
          {totalDaysLow > 0 && (
            <div className="mt-2 text-sm text-ink/60">
              {formatDays(totalDaysLow, lang)} {t.ofDev}
              {addonsTotalLow > 0 && t.optionsIncluded}
            </div>
          )}
          {grid.vatNote && <p className="mt-4 text-sm text-ink/60">{grid.vatNote}</p>}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-line bg-paper-pure shadow-sm">
          {jalons.map((group, g) => {
            const amount = group.lines.reduce((sum, l) => sum + l.amountLow, 0);
            const days = group.lines.reduce((sum, l) => sum + (l.daysLow || 0), 0);
            return (
              <div key={group.title || "projet"} className={cn(g > 0 && "border-t-2 border-line")}>
                <div className="flex items-baseline justify-between gap-4 bg-paper px-5 py-3 sm:px-7">
                  <div className="min-w-0">
                    {group.title && (
                      <div className="font-mono text-[0.75rem] uppercase tracking-widest text-gold">
                        {t.jalon} {g + 1}
                      </div>
                    )}
                    <div className="font-display text-xl text-ink">
                      {group.title || t.included}
                    </div>
                  </div>
                  {group.title && (
                    <div className="shrink-0 text-right">
                      <div className="font-display text-xl text-navy tabular-nums">
                        {formatEur(amount, lang)}
                      </div>
                      {days > 0 && <div className="text-sm text-ink/50">{formatDays(days, lang)}</div>}
                    </div>
                  )}
                </div>
                <ul>
                  {group.lines.map((line, i) => (
                    <li
                      key={line.id}
                      className={cn("px-5 py-4 sm:px-7", i > 0 && "border-t border-line")}
                    >
                      <div className="font-medium text-ink">{line.label}</div>
                      {line.description && (
                        <p className="mt-1 text-base leading-relaxed text-ink/65">
                          {line.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {addons.length > 0 && (
          <div className="mt-6 rounded-xl border border-line bg-paper-pure shadow-sm">
            <div className="px-5 pt-5 sm:px-7">
              <h3 className="font-display text-2xl text-ink">{t.options}</h3>
              <p className="mt-1 text-base text-ink/65">
                {t.optionsIntro}
              </p>
            </div>
            <ul className="mt-2">
              {addons.map((line) => {
                const isOn = !!selected[line.id];
                return (
                  <li key={line.id} className="border-t border-line first:border-t-0">
                    <button
                      type="button"
                      onClick={() => toggle(line.id)}
                      aria-pressed={isOn}
                      className="flex w-full gap-4 px-5 py-4 text-left transition-colors hover:bg-paper sm:px-7 sm:py-5"
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                          isOn ? "border-gold bg-gold text-navy-deep" : "border-ink/30 bg-transparent"
                        )}
                      >
                        {isOn && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-ink">{line.label}</span>
                        {line.description && (
                          <span className="mt-1 block text-base leading-relaxed text-ink/65">
                            {line.description}
                          </span>
                        )}
                      </span>
                      <span
                        className={cn(
                          "shrink-0 font-display text-lg tabular-nums transition-colors sm:text-xl",
                          isOn ? "text-navy" : "text-ink/45"
                        )}
                      >
                        + {formatEur(line.amountLow, lang)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h2
        className={cn(
          "font-display font-normal text-4xl sm:text-5xl text-center",
          dark ? "text-paper" : "text-ink"
        )}
      >
        {t.title}
      </h2>
      <p className={cn("mt-3 text-center", dark ? "text-paper/70" : "text-ink/70")}>
        {groups
          ? t.groupsIntro
          : hasRanges
            ? t.rangesIntro
            : t.pricesIntro}
      </p>
      <div className={cn("editorial-rule mt-6 mx-auto", dark && "editorial-rule--light")} />

      <div className="mt-10 overflow-hidden rounded-xl border border-line bg-paper-pure shadow-sm">
        {groups ? (
          groups.map((group, g) => {
            const amountLow = group.lines.reduce((sum, l) => sum + l.amountLow, 0);
            const amountHigh = group.lines.reduce((sum, l) => sum + l.amountHigh, 0);
            const daysLow = group.lines.reduce((sum, l) => sum + (l.daysLow || 0), 0);
            const daysHigh = group.lines.reduce((sum, l) => sum + (l.daysHigh || 0), 0);
            return (
              <div key={group.title || "autres"} className={cn(g > 0 && "border-t-2 border-line")}>
                <div className="flex items-baseline justify-between gap-4 bg-paper px-5 py-3 sm:px-7">
                  <div className="min-w-0">
                    {group.title && (
                      <div className="font-mono text-[0.75rem] uppercase tracking-widest text-gold">
                        {t.milestone} {g + 1}
                      </div>
                    )}
                    <div className="font-display text-xl text-ink">
                      {group.title || t.others}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-display text-xl text-navy tabular-nums">
                      {formatEurRange(amountLow, amountHigh, lang)}
                    </div>
                    {(daysLow > 0 || daysHigh > 0) && (
                      <div className="text-sm text-ink/50">{formatDaysRange(daysLow, daysHigh, lang)}</div>
                    )}
                  </div>
                </div>
                <ul>{group.lines.map((line, i) => renderBaseLine(line, i))}</ul>
              </div>
            );
          })
        ) : (
          <ul>{base.map((line, i) => renderBaseLine(line, i))}</ul>
        )}
      </div>

      {addons.length > 0 && (
        <div className="mt-6 rounded-xl border border-line bg-paper-pure shadow-sm">
          <div className="px-5 pt-5 sm:px-7">
            <h3 className="font-display text-2xl text-ink">{t.addons}</h3>
            <p className="mt-1 text-base text-ink/65">
              {t.addonsIntro}
            </p>
          </div>
          <ul className="mt-2">
            {addons.map((line) => {
              const isOn = !!selected[line.id];
              return (
                <li key={line.id} className="border-t border-line first:border-t-0">
                  <button
                    type="button"
                    onClick={() => toggle(line.id)}
                    aria-pressed={isOn}
                    className="flex w-full gap-4 px-5 py-4 text-left transition-colors hover:bg-paper sm:px-7 sm:py-5"
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
                        isOn
                          ? "border-gold bg-gold text-navy-deep"
                          : "border-ink/30 bg-transparent"
                      )}
                    >
                      {isOn && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-ink">{line.label}</span>
                      {line.description && (
                        <span className="mt-1 block text-base leading-relaxed text-ink/65">
                          {line.description}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span
                        className={cn(
                          "block font-display text-lg sm:text-xl tabular-nums transition-colors",
                          isOn ? "text-navy" : "text-ink/45"
                        )}
                      >
                        {formatEurRange(line.amountLow, line.amountHigh, lang)}
                      </span>
                      {(line.daysLow > 0 || line.daysHigh > 0) && (
                        <span className="mt-0.5 block text-sm text-ink/50">
                          {formatDaysRange(line.daysLow, line.daysHigh, lang)}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-gold/60 bg-paper-pure p-5 shadow-sm sm:p-7">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="font-display text-xl text-ink">{t.total}</div>
            {addons.length > 0 && (
              <div className="text-sm text-ink/60">{t.bySelection}</div>
            )}
          </div>
          <div className="text-right">
            <div className="font-display text-3xl sm:text-4xl text-navy tabular-nums">
              {formatEurRange(totalLow, totalHigh, lang)}
            </div>
            {(totalDaysLow > 0 || totalDaysHigh > 0) && (
              <div className="mt-1 text-sm text-ink/60">
                {formatDaysRange(totalDaysLow, totalDaysHigh, lang)} {t.ofDev}
              </div>
            )}
          </div>
        </div>
        {grid.vatNote && (
          <p className="mt-4 border-t border-line pt-4 text-sm text-ink/60">
            {grid.vatNote}
          </p>
        )}
      </div>
    </div>
  );
}
